import {
  DownOutlined,
  FileProtectOutlined,
  FilterOutlined,
  ReloadOutlined,
  UpOutlined,
} from "@ant-design/icons";
import { UserAuthz } from "@p0security/frontend-core";
import { getFirestoreDoc } from "@p0security/frontend-core/react";
import type {
  AgentActivityEntry,
  AgentActivityProtocol,
  AgentActivityRiskLevelParam,
  AgenticActivityFilters,
  AgenticActivityUnavailableReason,
  McpAgentActivityEntry,
} from "@p0security/integrations-agentic-shared/interface";
import {
  AGENTIC_ACTIVITY_FILTER_KEYS,
  AgentActivityProtocols,
  AgentActivityRiskLevelParams,
  SELECT_FILTER_PARAMS,
  TEXT_FILTER_PARAMS,
} from "@p0security/integrations-agentic-shared/interface";
import { integrationLabel } from "@p0security/shared/integrations/registry";
import { AppPaths } from "@p0security/shared/routes/constants";
import type { PermissionRequest } from "@p0security/shared/types/permission";
import type { RequestorRule } from "@p0security/shared/types/policy/types";
import {
  Alert,
  Badge,
  Button,
  Empty,
  Flex,
  Input,
  Select,
  Skeleton,
  Tooltip,
  Typography,
  message,
} from "@p0security/ui";
import type { CollectExportData } from "components/Export";
import { Tenant } from "components/Login";
import { ResizableTable, ResizableTableKeys } from "components/ResizableTable";
import { PERMISSION_REQUESTS_COLLECTION } from "firestore/constants";
import { startCase } from "lodash";
import {
  type ChangeEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  useHref,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import { AgenticClientsContext } from "../AgenticClientsContext";
import {
  EventDetailsDrawer,
  eventColumns,
  stringifyJson,
} from "./EventDetails.tsx";
import { EventLogExport } from "./EventLogExport.tsx";
import {
  EventFilterPanel,
  mergeFilterSuggestions,
  newFilterSuggestions,
  parseTimeParam,
} from "./EventLogFilters.tsx";
import { viewOf } from "./eventProtocols.tsx";
import { PAGE_SIZE_OPTIONS, useEventLog } from "./hooks/useEventLog.ts";
import type { TimeRange } from "./timeRange.ts";
import {
  DEFAULT_LOOKBACK,
  isDefaultTimeRange,
  isLookback,
} from "./timeRange.ts";

// `lookback` is a UI-only parameter: the API takes resolved bounds instead.
const FILTER_PARAMS = [...AGENTIC_ACTIVITY_FILTER_KEYS, "lookback"] as const;

const PAGE_SIZE_SELECT_OPTIONS = PAGE_SIZE_OPTIONS.map((value) => ({
  label: value.toString(),
  value,
}));

/** P0's own synthetic MCP server, used for access-check calls (e.g.
 * `check-access`, `list-access`) rather than the agent's real target tool. */
const P0_CONTROL_MCP_SERVER = "p0-access";

const isControlToolCall = (entry: McpAgentActivityEntry) =>
  entry.mcpServerName === P0_CONTROL_MCP_SERVER;

/** Whether an entry carries enough context (a real access request, agent,
 * and target tool) to prefill a policy from. An A2A call never does: it has
 * no tool and no access request to resolve a granted service from. */
const hasPolicyContext = (
  entry: AgentActivityEntry
): entry is McpAgentActivityEntry =>
  entry.protocol === "mcp" &&
  !isControlToolCall(entry) &&
  Boolean(entry.requestId) &&
  Boolean(entry.toolName) &&
  Boolean(entry.mcpServerName) &&
  Boolean(entry.clientId);

// The API rejects an unknown protocol, so drop anything a hand-edited URL
// carries rather than sending it.
const parseProtocolParam = (
  value: string | null
): AgentActivityProtocol | undefined =>
  AgentActivityProtocols.find((protocol) => protocol === value);

// Same reasoning as parseProtocolParam: the API rejects an unrecognized risk
// level.
const parseRiskParam = (
  value: string | null
): AgentActivityRiskLevelParam | undefined =>
  AgentActivityRiskLevelParams.find((level) => level === value);

const eventRowKey = (entry: AgentActivityEntry) =>
  entry.id ??
  [
    entry.timestamp,
    entry.sessionId,
    viewOf(entry).serverName(entry),
    entry.direction,
    entry.messageKind,
    entry.method,
    entry.toolName,
    entry.agentId,
  ].join(":");

const useEventQuery = () => {
  const [search, setSearch] = useSearchParams();
  const filterQuery = new URLSearchParams();
  for (const key of FILTER_PARAMS) {
    const value = search.get(key);
    if (value !== null) filterQuery.set(key, value);
  }
  const filterKey = filterQuery.toString();
  const { filters, timeRange } = useMemo(() => {
    const filterSearch = new URLSearchParams(filterKey);
    const startTime = parseTimeParam(filterSearch.get("startTime"));
    const endTime = parseTimeParam(filterSearch.get("endTime"));
    const lookback = filterSearch.get("lookback");
    return {
      filters: {
        agentId: filterSearch.get("agentId")?.trim() || undefined,
        caller: filterSearch.get("caller")?.trim() || undefined,
        clientId: filterSearch.get("clientId")?.trim() || undefined,
        mcpServerName: filterSearch.get("mcpServerName")?.trim() || undefined,
        protocol: parseProtocolParam(filterSearch.get("protocol")),
        risk: parseRiskParam(filterSearch.get("risk")),
        sessionId: filterSearch.get("sessionId")?.trim() || undefined,
        toolName: filterSearch.get("toolName")?.trim() || undefined,
      } satisfies AgenticActivityFilters,
      timeRange: (startTime && endTime
        ? { endTime, startTime, type: "custom" }
        : {
            lookback: isLookback(lookback) ? lookback : DEFAULT_LOOKBACK,
            type: "relative",
          }) satisfies TimeRange,
    };
  }, [filterKey]);

  const applyFilters = useCallback(
    (nextFilters: AgenticActivityFilters, nextTimeRange: TimeRange) => {
      setSearch((previous) => {
        const next = new URLSearchParams(previous);
        for (const key of FILTER_PARAMS) next.delete(key);
        for (const key of [...TEXT_FILTER_PARAMS, ...SELECT_FILTER_PARAMS]) {
          const value = nextFilters[key]?.trim();
          if (value) next.set(key, value);
        }
        if (nextTimeRange.type === "custom") {
          next.set("startTime", nextTimeRange.startTime.toISOString());
          next.set("endTime", nextTimeRange.endTime.toISOString());
        } else if (!isDefaultTimeRange(nextTimeRange)) {
          next.set("lookback", nextTimeRange.lookback);
        }
        return next;
      });
    },
    [setSearch]
  );

  const activeFilterCount =
    TEXT_FILTER_PARAMS.filter((key) => Boolean(filters[key])).length +
    SELECT_FILTER_PARAMS.filter((key) => Boolean(filters[key])).length +
    (isDefaultTimeRange(timeRange) ? 0 : 1);
  return { activeFilterCount, applyFilters, filterKey, filters, timeRange };
};

const matchesLocalSearch = (entry: AgentActivityEntry, search: string) => {
  const normalized = search.trim().toLocaleLowerCase();
  if (!normalized) return true;
  return Object.values(entry).some((value) => {
    const searchable =
      typeof value === "string"
        ? value
        : value && typeof value === "object"
          ? stringifyJson(value)
          : "";
    return searchable.toLocaleLowerCase().includes(normalized);
  });
};

/** Narrows collected export data by the local search box, keeping export rows
 * aligned with the event log table even though the API cannot apply this
 * client-side search itself. */
export const withSearchFilter =
  (
    collect: CollectExportData<AgentActivityEntry>,
    search: string
  ): CollectExportData<AgentActivityEntry> =>
  async (onProgress, cancellation) => {
    const collected = await collect(onProgress, cancellation);
    return collected.filter((entry) => matchesLocalSearch(entry, search));
  };

const isNestedInteractiveElement = (target: EventTarget) =>
  target instanceof Element && Boolean(target.closest("a, button"));

const formatCallCount = (count: number) =>
  `${count} call${count === 1 ? "" : "s"}`;

export const REQUEST_NOT_FOUND_ERROR =
  "Couldn't find the access request behind this event — it may have expired. Refresh and try again.";
const UNAVAILABLE_REASON_LABELS: Record<
  AgenticActivityUnavailableReason,
  string
> = {
  failed: "the query failed",
  timeout: "the gateway did not respond in time",
  unreachable: "the gateway or its log store could not be reached",
  unsupported:
    "the gateway's version or log configuration does not support activity queries",
};

const REQUEST_LOAD_ERROR =
  "Failed to load the access request for this event. Try again.";
/** The agentic Event Log tab — MCP and A2A events with shareable URL-backed
 * filters. Sources its clients from the overview outlet. */
export const EventLog: React.FC = () => {
  const { clients } = useContext(AgenticClientsContext);
  const { activeFilterCount, applyFilters, filterKey, filters, timeRange } =
    useEventQuery();
  const [filterSuggestions, setFilterSuggestions] =
    useState(newFilterSuggestions);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [localSearch, setLocalSearch] = useState("");
  const [selectedEntry, setSelectedEntry] = useState<AgentActivityEntry>();
  const {
    changePageSize,
    collect,
    entries,
    error,
    loadMore,
    loading,
    logProviderStatus,
    nextPageToken,
    pageSize,
    refresh,
    unavailableGateways,
  } = useEventLog(filters, timeRange);
  // Relative to this tab's fixed path (`agentic-access/event-log`), so the
  // router builds the tenant path rather than hand-assembling it.
  const logProviderHref = useHref("../../integrations/agentic/log-provider", {
    relative: "path",
  });
  // The API returns invocations only — MCP tool calls, A2A agent calls, and
  // any call the gateway rejected — so every entry it sends belongs here.
  const events = useMemo(() => entries ?? [], [entries]);
  const recommendableRowKeys = useMemo(() => {
    const latestByRequestId = new Map<string, AgentActivityEntry>();
    for (const entry of events) {
      if (!hasPolicyContext(entry)) continue;
      const requestId = entry.requestId;
      if (!requestId) continue;
      const current = latestByRequestId.get(requestId);
      if (!current || entry.timestamp > current.timestamp) {
        latestByRequestId.set(requestId, entry);
      }
    }
    return new Set(Array.from(latestByRequestId.values(), eventRowKey));
  }, [events]);
  const navigate = useNavigate();
  const { orgSlug } = useParams();
  const tenantId = useContext(Tenant);
  const [recommendingRowKey, setRecommendingRowKey] = useState<string>();
  const handleRecommendPolicy = useCallback(
    async (entry: McpAgentActivityEntry) => {
      // One recommendation at a time: a second row would navigate over the
      // first one's prefill, and whichever request settles first would clear
      // the other's loading state.
      if (recommendingRowKey) return;
      const { clientId, requestId } = entry;
      if (!requestId || !clientId) return;
      const rowKey = eventRowKey(entry);
      setRecommendingRowKey(rowKey);
      try {
        const request = await getFirestoreDoc<PermissionRequest>(
          `o/${tenantId}/${PERMISSION_REQUESTS_COLLECTION}/${requestId}`
        );
        if (!request) {
          message.error(REQUEST_NOT_FOUND_ERROR);
          return;
        }
        const headless = !!request.data.agentic?.headless;
        navigate(`/o/${orgSlug}/${AppPaths.Policies}/new`, {
          state: {
            prefill: {
              name: `${integrationLabel(request.data.type)} ${startCase(request.data.access)}`,
              requestor: {
                type: "agentic",
                actor: { type: "agent-client", clientId },
                subject:
                  !headless && entry.caller
                    ? {
                        type: "user",
                        user: { type: "user", uid: entry.caller },
                      }
                    : { type: "agent", agent: { type: "any" } },
              } satisfies RequestorRule,
              resource: {
                type: "integration",
                service: request.data.type,
                accessType: request.data.access,
                filters: {},
              },
            },
            resourceMode: "specific",
          },
        });
      } catch {
        message.error(REQUEST_LOAD_ERROR);
      } finally {
        setRecommendingRowKey(undefined);
      }
    },
    [navigate, orgSlug, recommendingRowKey, tenantId]
  );
  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (activeFilterCount === 0 && events.length > 0) {
      setFilterSuggestions((previous) =>
        mergeFilterSuggestions(previous, events)
      );
    }
  }, [activeFilterCount, events]);

  const handleApplyFilters = useCallback(
    (nextFilters: AgenticActivityFilters, nextTimeRange: TimeRange) => {
      applyFilters(nextFilters, nextTimeRange);
      setFiltersOpen(false);
    },
    [applyFilters]
  );
  const toggleFilters = useCallback(() => setFiltersOpen((open) => !open), []);
  const updateLocalSearch = useCallback(
    (event: ChangeEvent<HTMLInputElement>) =>
      setLocalSearch(event.target.value),
    []
  );
  const closeDetails = useCallback(() => setSelectedEntry(undefined), []);
  const eventRow = useCallback(
    (entry: AgentActivityEntry) => ({
      className:
        "cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[var(--ant-color-primary)]",
      onClick: (event: ReactMouseEvent<HTMLTableRowElement>) => {
        if (isNestedInteractiveElement(event.target)) return;
        setSelectedEntry(entry);
      },
      onKeyDown: (event: ReactKeyboardEvent<HTMLTableRowElement>) => {
        if (
          event.target !== event.currentTarget ||
          (event.key !== "Enter" && event.key !== " ")
        ) {
          return;
        }
        event.preventDefault();
        setSelectedEntry(entry);
      },
      tabIndex: 0,
    }),
    []
  );
  const visibleEntries = useMemo(
    () => events.filter((entry) => matchesLocalSearch(entry, localSearch)),
    [events, localSearch]
  );
  const hasLocalSearch = Boolean(localSearch.trim());
  const collectExport = useMemo(
    () => withSearchFilter(collect, localSearch),
    [collect, localSearch]
  );
  const onClick = useCallback(
    (entry: McpAgentActivityEntry) => () => handleRecommendPolicy(entry),
    [handleRecommendPolicy]
  );
  const userAuthz = useContext(UserAuthz);
  // Policy Studio's "new policy" route requires "owner", not just
  // "approver"/manager (Policies/Routes.tsx) — gate on the role that can
  // actually complete the flow, not just view Policy Studio.
  const canRecommendPolicy = userAuthz.has("owner");
  const baseColumns = useMemo(() => eventColumns(clients), [clients]);
  const columns = useMemo(
    () =>
      canRecommendPolicy
        ? [
            ...baseColumns,
            {
              key: "actions",
              render: (_: unknown, entry: AgentActivityEntry) => {
                const rowKey = eventRowKey(entry);
                return hasPolicyContext(entry) &&
                  recommendableRowKeys.has(rowKey) ? (
                  <Tooltip title="Review Recommended Policy">
                    <Button
                      aria-label="Review Recommended Policy"
                      disabled={
                        recommendingRowKey !== undefined &&
                        recommendingRowKey !== rowKey
                      }
                      icon={
                        <span className="text-[1.2em]">
                          <FileProtectOutlined />
                        </span>
                      }
                      loading={recommendingRowKey === rowKey}
                      onClick={onClick(entry)}
                      type="text"
                    />
                  </Tooltip>
                ) : null;
              },
              title: "",
              preventResize: true,
              width: 48,
            },
          ]
        : baseColumns,
    [
      baseColumns,
      recommendableRowKeys,
      recommendingRowKey,
      canRecommendPolicy,
      onClick,
    ]
  );
  const hasAppliedFilters = activeFilterCount > 0;

  return (
    <section aria-label="Agentic event log">
      <Flex gap={16} vertical>
        <Flex align="center" gap={12} justify="space-between" wrap>
          <div className="min-w-64 max-w-xl flex-1">
            <Input.Search
              allowClear
              aria-label="Search loaded events"
              placeholder="Search loaded events"
              value={localSearch}
              onChange={updateLocalSearch}
            />
          </div>
          <Flex gap={8} wrap>
            <Badge count={activeFilterCount} offset={[-2, 2]} size="small">
              <Button
                aria-controls="agentic-event-log-filters"
                aria-expanded={filtersOpen}
                aria-label={`${filtersOpen ? "Hide" : "Show"} filters${activeFilterCount ? `, ${activeFilterCount} applied` : ""}`}
                icon={<FilterOutlined />}
                onClick={toggleFilters}
              >
                Filters
                {filtersOpen ? <UpOutlined /> : <DownOutlined />}
              </Button>
            </Badge>
            <Flex align="center" gap={8}>
              <Typography.Text type="secondary">Page size</Typography.Text>
              <Select
                aria-label="Event log page size"
                className="w-20"
                options={PAGE_SIZE_SELECT_OPTIONS}
                value={pageSize}
                onChange={changePageSize}
              />
            </Flex>
            <Button icon={<ReloadOutlined />} onClick={refresh}>
              Refresh
            </Button>
            <EventLogExport
              collect={collectExport}
              hasLocalSearch={hasLocalSearch}
            />
          </Flex>
        </Flex>

        {filtersOpen && (
          <EventFilterPanel
            key={filterKey}
            initialFilters={filters}
            initialTimeRange={timeRange}
            suggestions={filterSuggestions}
            onApply={handleApplyFilters}
          />
        )}

        {error ? (
          <Alert
            action={
              <Button size="small" onClick={refresh}>
                Try again
              </Button>
            }
            message={error}
            showIcon
            type="error"
          />
        ) : entries === undefined ? (
          <div aria-label="Loading agentic event log" role="status">
            <Skeleton active paragraph={{ rows: 6 }} title={false} />
          </div>
        ) : (
          <Flex gap={24} vertical>
            {unavailableGateways.length > 0 && (
              <Alert
                description={
                  <ul className="m-0 pl-4">
                    {unavailableGateways.map(({ gatewayId, reason }) => (
                      <li key={gatewayId}>
                        {gatewayId}: {UNAVAILABLE_REASON_LABELS[reason]}
                      </li>
                    ))}
                  </ul>
                }
                message="Activity from some gateways is missing"
                showIcon
                type="warning"
              />
            )}
            <ResizableTable<AgentActivityEntry>
              className="[&_.ant-table-tbody>tr>td]:align-middle"
              columns={columns}
              dataSource={visibleEntries}
              locale={{
                emptyText:
                  logProviderStatus === "unconfigured" ? (
                    <Empty
                      description="No log provider is configured, so agent activity cannot be read."
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    >
                      <Button href={logProviderHref} type="primary">
                        Configure log provider
                      </Button>
                    </Empty>
                  ) : (
                    <Empty
                      description={
                        localSearch.trim()
                          ? "No loaded calls match your search."
                          : hasAppliedFilters
                            ? "No calls match these filters."
                            : "No calls in the last 7 days."
                      }
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  ),
              }}
              pagination={false}
              onRow={eventRow}
              rowKey={eventRowKey}
              tableId={ResizableTableKeys.agenticEventLog}
            />
            {(events.length > 0 || nextPageToken) && (
              <Flex align="center" gap={12} justify="center" wrap>
                <Typography.Text aria-live="polite" type="secondary">
                  {localSearch.trim()
                    ? `${visibleEntries.length} of ${events.length} loaded call${events.length === 1 ? "" : "s"} match your search.`
                    : `${formatCallCount(events.length)} loaded${hasAppliedFilters ? ` with ${activeFilterCount} applied filter${activeFilterCount === 1 ? "" : "s"}` : " from the last 7 days"}.`}
                </Typography.Text>
                {nextPageToken && (
                  <Button loading={loading} onClick={loadMore}>
                    Load more events
                  </Button>
                )}
              </Flex>
            )}
          </Flex>
        )}
      </Flex>
      <EventDetailsDrawer
        clients={clients}
        entry={selectedEntry}
        onClose={closeDetails}
      />
    </section>
  );
};
